import React, { useMemo, useState } from "react";
// Meter Events Component

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

// Custom
import CustomDataTable from "../table/CustomTable";
import useMeterEventsV2 from "../../hooks/useMeterEventsV2";
import { formatDate } from "../../utils/helper";
import moment from "moment";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import Calendar2 from "../../assets/svg/calendar2";

interface MeterEventsProps {
  meterSerialNumber: string;
}

const MeterEvents: React.FC<MeterEventsProps> = ({ meterSerialNumber }) => {
  const [startDate, setStartDate] = useState<Date | null>(
    moment().subtract(7, "days").toDate(),
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateEl, setDateEl] = useState<HTMLElement | null>(null);
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);

  // These are the dates used for the API call
  const [queryDates, setQueryDates] = useState({
    start: moment().subtract(7, "days").toDate(),
    end: new Date(),
  });

  const { data: eventsResponse, isLoading, refetch } = useMeterEventsV2({
    meterSerialNumber,
    from: moment(queryDates.start)
      .startOf("day")
      .add(timeRange[0], "minutes")
      .format("YYYY-MM-DDTHH:mm"),
    to: moment(queryDates.end)
      .startOf("day")
      .add(timeRange[1], "minutes")
      .format("YYYY-MM-DDTHH:mm"),
    skip: page * rowsPerPage,
    take: rowsPerPage,
  });

  const eventsData = eventsResponse?.data?.records || [];
  const totalCount = eventsResponse?.data?.total || 0;

  const COLUMNS = ["Date/Time", "Event Name", "Description", "Type", "Code"];

  const rowsContent = useMemo(() => {
    return eventsData.map((item, index) => (
      <TableRow key={item.id || index}>
        <TableCell>
          <Typography fontSize="13px">
            {formatDate(item.eventTimestamp, "DD/MM/YYYY HH:mm:ss")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px" fontWeight={600}>
            {item.eventName || "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px">{item.description || "N/A"}</Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px">{item.eventType || "N/A"}</Typography>
        </TableCell>
        <TableCell>
          <Typography fontSize="13px">{item.eventCode || "N/A"}</Typography>
        </TableCell>
      </TableRow>
    ));
  }, [eventsData]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleDateSubmit = () => {
    if (startDate && endDate) {
      setQueryDates({ start: startDate, end: endDate });
      setPage(0);
    }
  };

  return (
    <Card
      sx={{
        borderWidth: "1px",
        borderColor: "#E0E0E0",
        borderRadius: 2,
        m: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pr: 2 }}
      >
        <CardHeader title="Meter Events" />
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontSize="13px" fontWeight={500} color="textSecondary">
            {formatDate(queryDates.start, "D MMM YYYY")} -{" "}
            {formatDate(queryDates.end, "D MMM YYYY")}
          </Typography>
          <IconButton onClick={(e) => setDateEl(e.currentTarget)}>
            <Calendar2 />
          </IconButton>
          <DateAndTimePopover
            anchorEl={dateEl}
            setAnchorEl={setDateEl}
            startDate={startDate as Date}
            endDate={endDate as Date}
            handleDateChange={handleDateChange}
            timeRange={timeRange}
            setTimeRange={(val) => setTimeRange(val as number[])}
            handleDateSubmit={handleDateSubmit}
          />
        </Stack>
      </Stack>
      <CardContent sx={{ p: 0, borderTop: "1px solid #E0E0E0" }}>
        <CustomDataTable
          columns={COLUMNS}
          rowsContent={rowsContent}
          isLoading={isLoading}
          totalCount={totalCount}
          pagination={true}
          Actions={true}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sxHeader={{ height: "40px" }}
        />
      </CardContent>
    </Card>
  );
};

export default MeterEvents;
